import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph, MORPH_OPTIONS } from '@theme/morph';

const GRANDCHILDREN_SECTION_ID = 'section-rendering-collection-categories';
const GRANDCHILDREN_SELECTOR = '[data-drawer-grandchildren-handle]';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#updateDrawerTopOffset();
    }

    summary.setAttribute('aria-expanded', 'true');

    // Lazy-hydrate the band grid for genre submenus the first time they open.
    // Fire-and-forget — the morph lands when the response arrives, the open
    // animation doesn't wait for it.
    if (target === 'submenu') {
      this.#hydrateGrandchildren(details);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Fetch and morph the per-collection band grid the first time the user
   * opens a genre's submenu in the drawer. Mirrors the desktop pattern in
   * `header-menu.js#renderSubCategories` — small, dedicated SRA section
   * (`section-rendering-mega-menu-drawer`), `sectionRenderer` for caching
   * and request dedup, `morph` so any in-flight image loads or focus state
   * survive subsequent re-opens.
   *
   * @param {HTMLDetailsElement} details - The details element being opened.
   */
  async #hydrateGrandchildren(details) {
    // Find an empty placeholder <ul> that belongs *directly* to this details —
    // i.e. not inside a nested <details> further down (which has its own
    // collection handle and gets hydrated when the user drills into it).
    const candidates = details.querySelectorAll(`${GRANDCHILDREN_SELECTOR}:not([data-loaded])`);
    /** @type {HTMLElement | null} */
    let target = null;
    for (const candidate of candidates) {
      if (candidate instanceof HTMLElement && candidate.closest('details') === details) {
        target = candidate;
        break;
      }
    }
    if (!target) return;

    const handle = target.dataset.drawerGrandchildrenHandle;
    if (!handle) return;

    // Mark immediately so a rapid re-open doesn't fire a parallel fetch.
    // `sectionRenderer` would dedupe the network call anyway, but skipping
    // the morph saves work.
    target.dataset.loaded = 'pending';

    const url = new URL(`/collections/${handle}`, window.location.origin);
    let html;
    try {
      html = await sectionRenderer.getSectionHTML(GRANDCHILDREN_SECTION_ID, true, url);
    } catch (error) {
      delete target.dataset.loaded;
      console.error('Failed to load drawer subcategories for', handle, error);
      return;
    }

    // Element may have been morphed away (section hydration) while we awaited.
    if (!target.isConnected) return;

    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const newList = fragment.querySelector('ul');
    if (!newList) {
      delete target.dataset.loaded;
      return;
    }

    newList.querySelectorAll('li').forEach(el => {
      el.classList.add('menu-drawer__list-item')
    })

    // `childrenOnly: true` keeps `target` itself (so this method's marker and
    // any styles/listeners on it survive) and morphs its contents to match
    // the fetched <ul>'s contents.
    morph(target, newList, MORPH_OPTIONS);
    target.dataset.loaded = 'true';
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Pin the drawer to the bottom edge of the visible header.
   * Uses the live bounding rect of the (sticky) header so a scrolled-away
   * announcement bar is not counted, and a still-visible announcement bar is.
   */
  #updateDrawerTopOffset() {
    const header = document.querySelector('header-component');
    if (!(header instanceof HTMLElement)) return;
    const offset = Math.max(0, header.getBoundingClientRect().bottom);
    this.style.setProperty('--menu-drawer-top-offset', `${offset}px`);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
